import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaStore, FaUserTie, FaQrcode, FaEdit, FaBan, FaArrowUp } from "react-icons/fa";
import { Chart, registerables } from "chart.js";
import "../styles/OrganizationDetails.css";
import { getOrganizationStats, getOrgDetailDashboard, getOrgAuditTimeline } from "../api/api";
import { useToast } from "../context/ToastContext";

Chart.register(...registerables);

/* Zero-value defaults merged under whatever the API returns — no fake data */
const DEFAULT_STATS = {
  totalMerchants: 0,
  activeMerchants: 0,
  totalAgents: 0,
  totalDynamicQR: 0,
  totalStaticQR: 0,
  txVolume: 0,
  volumeChangePct: 0,
  healthScore: 0,
  adoptionRate: 0,
  offlineTerminals: 0,
  volumeSeries: [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0, 0, 0, 0, 0,
  ],
  auditTimeline: [],
  risk: [],
};

/* ── colour tokens ── */
const C = {
  purple: "#534AB7",
  teal:   "#1D9E75",
  red:    "#D85A30",
  amber:  "#BA7517",
  blue:   "#1976D2",
};

const TILE_CONFIG = [
  { key: "totalMerchants",  label: "Total Merchants",   icon: <FaStore />,   accent: C.purple },
  { key: "totalDynamicQR",  label: "Dynamic QRs",       icon: <FaQrcode />,  accent: C.teal   },
  { key: "totalStaticQR",   label: "Static QRs",        icon: <FaQrcode />,  accent: C.amber  },
  { key: "activeMerchants", label: "Active Merchants",  icon: <FaStore />,   accent: C.blue   },
  { key: "totalAgents",     label: "Total Agents",      icon: <FaUserTie />, accent: C.red    },
];

/* ── dark-mode hook ── */
function useDark() {
  const [dark, setDark] = useState(window.matchMedia("(prefers-color-scheme:dark)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme:dark)");
    const h = (e) => setDark(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return dark;
}

/* ── generic Chart.js wrapper ── */
function CJSChart({ id, build, height = 260 }) {
  const ref = useRef(null);
  const inst = useRef(null);
  const dark = useDark();

  useEffect(() => {
    if (!ref.current) return;
    inst.current?.destroy();
    inst.current = build(ref.current, dark);
    return () => inst.current?.destroy();
  }, [dark, build]);

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      <canvas ref={ref} id={id} />
    </div>
  );
}

/* ── stat card ── */
const StatTile = ({ label, value, icon, accent }) => (
  <div className="od-tile" style={{ "--accent": accent }}>
    <div className="od-tile__icon" style={{ background: accent + "22", color: accent }}>
      {icon}
    </div>
    <div className="od-tile__body">
      <p className="od-tile__label">{label}</p>
      <p className="od-tile__value">{value?.toLocaleString() ?? 0}</p>
    </div>
    <div className="od-tile__bar" style={{ background: accent }} />
  </div>
);

/* ══════════════════════════════════════════ */
const OrganizationDetails = ({ orgId: propOrgId, orgName: propOrgName, isSidebarOpen, mode = "page" }) => {
  const isDrawer = mode === "drawer";
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showError } = useToast();

  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};
  const orgId = propOrgId ?? state.orgId ?? null;
  const orgName = propOrgName ?? state.name ?? "Organization";

  useEffect(() => {
    const normalizeTimelineItem = (item) => ({
      title: item.title || item.action || item.event || "Activity",
      source: item.source || item.actor || item.performedBy || item.username || "System",
      time:
        item.time ||
        (item.timestamp || item.createdAt
          ? new Date(item.timestamp || item.createdAt).toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : ""),
      detail: item.detail || item.description || item.details || item.message || "",
    });

    const fetchData = async () => {
      try {
        // Primary source: detail dashboard endpoint; falls back to /org/:id
        let data;
        try {
          const res = await getOrgDetailDashboard(orgId);
          data = res?.data ?? res;
        } catch (dashErr) {
          console.error("Error fetching detail dashboard, falling back to org stats:", dashErr);
          const res = await getOrganizationStats(orgId);
          data = res?.data;
        }

        // Audit timeline comes from its own endpoint; non-fatal if it fails
        let auditTimeline = [];
        try {
          const auditRes = await getOrgAuditTimeline(orgId, 20);
          const auditPayload = auditRes?.data ?? auditRes;
          const auditItems = Array.isArray(auditPayload)
            ? auditPayload
            : Array.isArray(auditPayload?.timeline)
              ? auditPayload.timeline
              : Array.isArray(auditPayload?.data)
                ? auditPayload.data
                : [];
          auditTimeline = auditItems.map(normalizeTimelineItem);
        } catch (auditErr) {
          console.error("Error fetching audit timeline:", auditErr);
        }

        setStats({
          ...DEFAULT_STATS,
          ...(data || {}),
          ...(auditTimeline.length ? { auditTimeline } : {}),
        });
      } catch (err) {
        console.error("Error fetching stats:", err);
        setStats(null);
        showError("Error", err?.response?.data?.message || "Failed to load organization details", 3000);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  if (loading) {
    return (
      <div className={`od-container ${isSidebarOpen ? "shifted" : ""}`}>
        <p className="loader">Loading details...</p>
      </div>
    );
  }
  if (!stats) {
    return (
      <div className={`od-container ${isSidebarOpen ? "shifted" : ""}`}>
        <p>No stats available</p>
      </div>
    );
  }

  const totalQRs       = (stats.totalDynamicQR ?? 0) + (stats.totalStaticQR ?? 0);
  const inactive       = (stats.totalMerchants ?? 0) - (stats.activeMerchants ?? 0);
  const activeMer      = stats.activeMerchants ?? 0;
  const totalMer       = Math.max(1, stats.totalMerchants ?? 1);
  const derivedHealth  = Math.round((activeMer / totalMer) * 100);
  const healthPct      = Math.max(0, Math.min(100, (stats.healthScore ?? derivedHealth)));
  const txnVolume      = stats.txVolume ?? stats.transactionVolume ?? 0;
  const volumeChange   = stats.volumeChangePct ?? 0;
  const volumeSeries   = Array.isArray(stats.volumeSeries) && stats.volumeSeries.length ? stats.volumeSeries : DEFAULT_STATS.volumeSeries;
  const auditTimeline  = Array.isArray(stats.auditTimeline) ? stats.auditTimeline : [];
  const riskIndicators = Array.isArray(stats.risk) ? stats.risk : [];

  const currency = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);

  /* chart builders — stable refs via useCallback-style inline fns passed each render;
     CJSChart keys on `id` so it remounts when data changes */
  const buildVolume = (canvas, dark) => {
    const ctx = canvas.getContext("2d");
    const gradient = ctx.createLinearGradient(0, 0, 0, 260);
    gradient.addColorStop(0, "rgba(0, 87, 255, 0.35)");
    gradient.addColorStop(1, "rgba(0, 87, 255, 0)");
    const grid = "rgba(255,255,255,.07)";
    const text = "#a8a09a";
    return new Chart(canvas, {
      type: "line",
      data: {
        labels: volumeSeries.map((_, i) => i + 1),
        datasets: [{
          data: volumeSeries,
          borderColor: "#0057ff",
          backgroundColor: gradient,
          tension: 0.35,
          fill: true,
          pointRadius: 0,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.y.toFixed(2)}M` } } },
        scales: {
          x: { grid: { color: "transparent" }, border: { display: false }, ticks: { display: false } },
          y: { grid: { color: grid }, border: { display: false }, ticks: { color: text, callback: (v) => `${v.toFixed(1)}M` } },
        },
      },
    });
  };

  const buildGauge = (canvas) => {
    return new Chart(canvas, {
      type: "doughnut",
      data: {
        labels: ["Health", "Remainder"],
        datasets: [{
          data: [healthPct, 100 - healthPct],
          backgroundColor: ["#34d6be", "#0e1b2f"],
          borderWidth: 0,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false, cutout: "78%",
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
      },
    });
  };

  // ── simple adoption bar width helper (0..100)
  const pct = (v) => `${Math.max(0, Math.min(100, v))}%`;

  const QR_LEGEND   = [{ color: C.purple, label: "Dynamic" }, { color: C.teal, label: "Static" }];
  const ACT_LEGEND  = [{ color: C.teal, label: "Active" }, { color: C.red, label: "Inactive" }, { color: C.purple, label: "Agents" }];
  const OVW_LEGEND  = [
    { color: C.purple, label: "Total Merchants" },
    { color: C.teal,   label: "Active" },
    { color: C.red,    label: "Inactive" },
    { color: C.amber,  label: "Agents" },
    { color: C.blue,   label: "QRs" },
  ];

  if (isDrawer) {
    return (
      <div className="od-drawer-body">
        {/* Stat cards */}
        <div className="od-drawer-stats">
          <div className="od-drawer-stat">
            <div className="od-drawer-stat__value">{(stats.activeMerchants ?? 0).toLocaleString()}</div>
            <div className="od-drawer-stat__label">Active Merchants</div>
            <div className="od-drawer-stat__sub">+{(stats.totalMerchants ?? 0) - (stats.activeMerchants ?? 0)} Growth</div>
          </div>
          <div className="od-drawer-stat">
            <div className="od-drawer-stat__value">{(stats.totalDynamicQR ?? 0).toLocaleString()}</div>
            <div className="od-drawer-stat__label">Dynamic QRs</div>
            <div className="od-drawer-stat__sub">+{(stats.totalStaticQR ?? 0).toLocaleString()} Generated</div>
          </div>
        </div>

        {/* Volume chart */}
        <div className="od-drawer-card">
          <div className="od-drawer-card__eyebrow">30-Day Volume Trend</div>
          <div className="od-chart-wrap" style={{ height: 160 }}>
            <CJSChart id="od-drawer-volume" build={buildVolume} height={160} />
          </div>
        </div>

        {/* Merchant Distribution */}
        <div className="od-drawer-card">
          <div className="od-drawer-card__eyebrow">Merchant Distribution</div>
          <div className="od-drawer-bars">
            {[
              { label: "Retail", value: Math.round((activeMer / Math.max(1, totalMer)) * 100), color: "#00c8ff" },
              { label: "Services", value: Math.round((inactive / Math.max(1, totalMer)) * 100), color: "#f59e0b" },
              { label: "Other", value: 0, color: "#8b5cf6" },
            ].map((b) => (
              <div key={b.label} className="od-drawer-bar">
                <div className="od-drawer-bar__track">
                  <div className="od-drawer-bar__fill" style={{ width: `${b.value}%`, background: b.color }} />
                </div>
                <div className="od-drawer-bar__label">
                  <span>{b.label}</span>
                  <span style={{ color: b.color }}>{b.value}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Security & Audits */}
        <div className="od-drawer-card">
          <div className="od-drawer-card__eyebrow">Security &amp; Audits</div>
          <div className="od-drawer-audit">
            {!auditTimeline.length ? (
              <div className="od-muted">No audit activity yet.</div>
            ) : null}
            {auditTimeline.slice(0, 2).map((it, idx) => (
              <div key={idx} className="od-drawer-audit__item">
                <div className="od-drawer-audit__icon">
                  {idx === 0 ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#1D9E75"/><path d="M5 8L7 10L11 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect width="16" height="16" rx="4" fill="#534AB7"/><path d="M8 4V12M4 8H12" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  )}
                </div>
                <div className="od-drawer-audit__body">
                  <div className="od-drawer-audit__title">{it.title}</div>
                  <div className="od-drawer-audit__meta">{it.time} • {it.source}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    );
  }

  return (
    <div className={`od-page ${isSidebarOpen ? "shifted" : ""}`}>
      {/* ── COMPACT HERO HEADER ────────────────────── */}
      <section className="od-hero">
        <div className="od-hero__top">
          <div className="od-hero__org">
            <div className="od-hero__avatar">{String(orgName).slice(0,1)}</div>
            <div>
              <div className="od-hero__name">{orgName}</div>
              <div className="od-hero__sub">Stockholm, Sweden • ID KE-9823PL-6</div>
            </div>
          </div>
          <div className="od-hero__actions">
            <button className="od-action od-action--ghost" onClick={() => navigate("/dashboard/organization?mode=edit", { state: { mode: "edit", merchant: { id: orgId, name: orgName } } })}>
              <FaEdit /> Edit Details
            </button>
            <button className="od-action od-action--danger" onClick={() => console.log("Block Access clicked") }>
              <FaBan /> Block Access
            </button>
          </div>
        </div>
      </section>

      {/* ── HEALTH + VOLUME ROW ────────────────────── */}
      <section className="od-section-grid od-section-grid--2">
        {/* Health */}
        <div className="od-card">
          <div className="od-card__header"><div className="od-card__eyebrow">Organization</div><h3 className="od-card__title">Health</h3></div>
          <div className="od-gauge">
            <CJSChart id="od-gauge" build={buildGauge} height={180} />
            <div className="od-gauge__center">
              <div className="od-gauge__pct">{healthPct}%</div>
              <div className="od-gauge__label">Optimal</div>
            </div>
          </div>
          <div className="od-muted">Health increased by 0% since the last audit cycle.</div>
        </div>

        {/* Volume */}
        <div className="od-card od-card--wide">
          <div className="od-card__header">
            <div className="od-card__eyebrow">Transaction Volume</div>
            <div className="od-hero__metric" style={{ marginTop: 0 }}>
              <div className="od-hero__metricvalue">{currency(txnVolume)}</div>
              <span className="od-chip od-chip--up"><FaArrowUp />+{volumeChange}%</span>
            </div>
          </div>
          <div className="od-chart-wrap">
            <CJSChart id="od-volume" build={buildVolume} height={220} />
          </div>
        </div>
      </section>

      {/* ── THREE UP GRID ───────────────────────────── */}
      <section className="od-section-grid od-section-grid--3">
        {/* Merchant Status */}
        <div className="od-card">
          <div className="od-card__header"><div className="od-card__eyebrow">Merchant Status</div></div>
          <div className="od-row">
            <div className="od-row__label">Active Terminals</div>
            <div className="od-row__value">{stats.activeMerchants ?? 0}</div>
          </div>
          <div className="od-progress">
            <div className="od-progress__fill od-progress__fill--teal" style={{ width: pct((stats.activeMerchants ?? 0) / Math.max(1, stats.totalMerchants ?? 1) * 100) }} />
          </div>
          <div className="od-row od-row--spaced">
            <div className="od-row__label">Offline/Inactive</div>
            <div className="od-row__value">{inactive}</div>
          </div>
          <div className="od-progress">
            <div className="od-progress__fill od-progress__fill--amber" style={{ width: pct((inactive / Math.max(1, stats.totalMerchants ?? 1)) * 100) }} />
          </div>
        </div>

        {/* QR Adoption */}
        <div className="od-card">
          <div className="od-card__header"><div className="od-card__eyebrow">QR Adoption</div></div>
          <div className="od-qr-row">
            <img src="/assets/qr-icon.png" alt="QR" className="od-qr-icon" onError={(e) => { e.target.style.display='none'; }} />
            <div className="od-qr-body">
              <div className="od-adoption-pct">{stats.adoptionRate ?? 0}%</div>
              <div className="od-adoption-label">Total Adoption</div>
              <div className="od-adotion-sub">0% this week</div>
            </div>
          </div>
          <div className="od-kpis">
            <div className="od-kpi"><span className="od-kpi__value">{stats.adoptionRate ?? 0}%</span><span className="od-kpi__label">Total Adoption</span></div>
            <div className="od-kpi"><span className="od-kpi__value">{stats.totalMerchants ?? 0}</span><span className="od-kpi__label">Total Merchants</span></div>
          </div>
        </div>

        {/* Timeline */}
        <div className="od-card">
          <div className="od-card__header"><div className="od-card__eyebrow">Audit</div><h3 className="od-card__title">Timeline</h3></div>
          <ul className="od-timeline">
            {!auditTimeline.length ? (
              <li className="od-muted">No audit activity yet.</li>
            ) : null}
            {auditTimeline.map((it, idx) => (
              <li key={idx} className="od-timeline__item">
                <span className="od-timeline__dot" />
                <div className="od-timeline__content">
                  <div className="od-timeline__title">{it.title}</div>
                  <div className="od-timeline__meta">{it.source} • {it.time}</div>
                  <div className="od-timeline__text">{it.detail}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="od-timeline__footer"><span>View All Activity</span></div>
        </div>
      </section>

      {/* ── RISK ROW ───────────────────────────────── */}
      {riskIndicators.length ? (
      <section className="od-risk-section">
        <div className="od-risk__eyebrow">Risk Indicators</div>
        <div className="od-riskbar">
          {riskIndicators.map((r, i) => (
            <div key={i} className={`od-pill ${r.tone === "good" ? "od-pill--good" : r.tone === "warn" ? "od-pill--warn" : ""}`}>
              <span className={`od-pill__dot od-pill__dot--${r.tone}`} />
              <div className="od-pill__body">
                <span className="od-pill__label">{r.label}</span>
                <span className="od-pill__value">{r.value}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
      ) : null}
    </div>
  );
};

export default OrganizationDetails;
